<template>
  <section class="block">
    <nav
      :class="scrolled >= 300 ? 'fixed top-0 inset-x-0' : 'hidden'"
      style="box-shadow: 0px 2px 2px 0px rgba(151, 151, 153, 0.12)"
      class="top-[62px] lg:top-[88px] cos product__subnav bg-white text-md xl:text-xl uppercase lg:px-6 z-[9002] animate-fade"
    >
      <client-only>
        <ScrollObserveActive
          class="container whitespace-nowrap"
          :offset="148"
          :modify-url="false"
        >
          <!-- Product name -->
          <template #default>
            <div
              class="flex flex-col items-center justify-between py-4 lg:flex-row lg:border-t lg:border-light-gray"
            >
              <div
                class="items-center text-center lg:mx-0 lg:text-left mb-[14px] pb-2 lg:mb-0 lg:pb-0 border-b border-[#E6E6E6] lg:border-none gap-2 w-full lg:w-auto font-ff-good-pro-cond font-semibold lg:font-normal italic lg:not-italic text-black uppercase text-lg lg:flex"
              >
                <span>{{ props.productName }}</span>
                <Dropdown
                  v-if="props.productItem.length > 1"
                  align="left"
                  :disabled="props.productItem.length < 1"
                >
                  <template #button-content="{ onClickOpen }">
                    <button
                      class="flex items-center space-s-2 focus:outline-none"
                      @click.prevent="onClickOpen"
                    >
                      <ChevronDownIcon
                        v-if="props.productItem.length > 1"
                        class="w-4 h-4"
                      />
                    </button>
                  </template>

                  <div
                    v-if="props.productItem.length > 1"
                    class="py-1 divide-y divide-gray-100"
                  >
                    <nuxt-link
                      v-for="product in products"
                      :key="product.slug"
                      :to="$i18nPath(`/products/${product.slug}`)"
                      class="flex items-center px-4 py-2 text-base text-black space-s-2 button hover:no-underline"
                    >
                      <img
                        v-if="product.image"
                        :src="product.image"
                        class="w-16"
                      />
                      <span class="text-xs" v-text="product.name" />
                    </nuxt-link>
                  </div>
                </Dropdown>
              </div>

              <div
                class="flex items-center h-5 px-4 overflow-x-auto md:overflow-hidden no-scrollbar sm:justify-center"
              >
                <div
                  class="flex flex-row items-center gap-6 pr-5 leading-7 lg:gap-4 flex-nowrap subsections lg:pr-0"
                >
                  <!-- Top specs -->
                  <span
                    class="text-black scrollactive-item"
                    :class="scrollItemClass"
                    data-id="top-specs"
                  >
                    {{ $t("common.top_specs") }}
                  </span>

                  <!-- Introduction -->
                  <span
                    v-if="props.hasIntroduction"
                    class="text-black scrollactive-item"
                    :class="scrollItemClass"
                    data-id="introduction"
                  >
                    {{ $t("common.introduction") }}
                  </span>

                  <!-- Top features -->
                  <span
                    v-if="props.hasTopFeatures"
                    class="text-black scrollactive-item"
                    :class="scrollItemClass"
                    data-id="topFeatures"
                  >
                    {{ $t("common.top_features") }}
                  </span>

                  <!-- Photos and videos -->
                  <span
                    v-if="props.hasGallery"
                    class="text-black scrollactive-item"
                    :class="scrollItemClass"
                    data-id="gallery"
                  >
                    {{ $t("common.gallery") }}
                  </span>

                  <!-- Specifications -->
                  <span
                    v-if="props.hasSpecifications"
                    class="text-black scrollactive-item"
                    :class="scrollItemClass"
                    data-id="specifications"
                  >
                    {{ $t("common.tech_specs") }}
                  </span>

                  <!-- Geometries -->
                  <!-- <span
                  v-if="hasGeometries"
                  @click="openTab('specifications')"
                  class="text-black scrollactive-item"
                  :class="scrollItemClass"
                >
                  {{ $t('common.geometries') }}
                </span> -->

                  <!-- Downloads -->
                  <!-- <span
                  v-if="hasManuals"
                  @click="openTab('manuals')"
                  class="text-sm text-base text-white scrollactive-item"
                >
                  {{ $t('common.downloads') }}
                </span> -->
                  <span
                    v-if="props.hasFaqs"
                    class="text-black scrollactive-item"
                    :class="scrollItemClass"
                    data-id="faq"
                  >
                    {{ $t("common.faq") }}
                  </span>
                  <!-- Buy now -->
                  <Btn
                    v-if="mainStore.enableShop && props.inventoryTotal >= 1"
                    class="px-10 ms-6"
                    @click.prevent="openInventoriesModal"
                  >
                    {{ $t("common.buy") }}
                  </Btn>
                </div>
              </div>
            </div>
          </template>
        </ScrollObserveActive>
      </client-only>
    </nav>
  </section>
</template>

<script setup>
import ChevronDownIcon from "~/assets/svg/heroicons/chevron-down.svg?component";
import InventoriesModal from "~/modals/InventoriesModal";
import { useModal } from "vue-final-modal";
// import { useProductMenuStore } from "@/store/productmenu";
import { useMainStore } from "@/store/index";

const props = defineProps({
  productItem: {
    type: Object,
    required: true,
  },
  productCategory: {
    type: String,
    required: true,
  },
  inventoryTotal: {
    type: Number,
    required: false,
  },
  productName: {
    type: String,
    required: true,
  },
  productSlug: {
    type: String,
    required: true,
  },
  hasIntroduction: {
    type: Boolean,
    default: false,
  },
  hasFaqs: {
    type: Boolean,
    default: false,
  },
  hasGallery: {
    type: Boolean,
    default: false,
  },
  hasTopFeatures: {
    type: Boolean,
    default: false,
  },
  hasSpecifications: {
    type: Boolean,
    default: false,
  },
});

// const productMenuStore = useProductMenuStore()
const mainStore = useMainStore();

const scrolled = ref(0);

const products = computed(() => {
  // All, Carbon, etc
  const productsByType = mainStore.products.filter(
    (e) => e.name === props.productCategory
  )[0].products;
  return Object.keys(productsByType).flatMap((key) => productsByType[key]);
});

const scrollItemClass = computed(() => {
  const locale = mainStore.locale;
  if (locale === "es" || locale === "gr") {
    return "text-sm lg:text-base";
  }
  return "text-sm md:text-lg";
});

onBeforeMount(() => {
  if (process.browser) {
    window.addEventListener("scroll", handleScroll, false);
  }
});

onUnmounted(() => {
  if (process.browser) {
    window.removeEventListener("scroll", handleScroll);
  }
});

function handleScroll() {
  scrolled.value = window.pageYOffset !== undefined ? window.scrollY : "";
}

function openTab(tab) {
  const sliderPagination = document.querySelector(".tab__pagination");
  let selectedTab = 0;

  switch (tab) {
    case "geometries":
      selectedTab = 1;
      break;
    case "manuals":
      selectedTab = 2;
      break;
    default:
      selectedTab = 0;
      break;
  }
  sliderPagination.children.item(selectedTab).click();
}

const { open, close } = useModal({
  component: InventoriesModal,
  attrs: {
    productSlug: props.productSlug,
  },
});

const openInventoriesModal = open;
</script>

<style lang="postcss">
.v--modal-block-scroll {
  @apply overflow-y-hidden;

  .product__subnav {
    @apply hidden;
  }
}

.product__subnav {
  .subsections span {
    animation: fade 0.3s ease-in-out;

    &.scrollactive-item {
      @apply font-ff-good-pro-cond lg:font-normal text-[#000]  font-medium uppercase  lg:capitalize cursor-pointer;

      &.is-active {
        @apply text-primary font-medium;
      }
    }
  }
}
</style>
